/**
 * 用户引导 Store
 * 管理用户引导状态
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import type { CSSProperties } from 'vue';
import { getGuideSteps, getPageInfo, isPageSupportedForGuide, type GuideStep } from '@/utils/guide';

export const useGuideStore = defineStore('guide', () => {
  // 状态
  const showUserGuide = ref(false);
  const showWelcomeDialog = ref(false);
  const currentGuideStep = ref(0);
  const guideSteps = ref<GuideStep[]>([]);

  // 计算属性
  const route = useRoute();

  const currentPageTitle = computed(() => {
    const pageInfo = getPageInfo(route.path);
    return pageInfo.title;
  });

  const currentPageDescription = computed(() => {
    const pageInfo = getPageInfo(route.path);
    return pageInfo.description;
  });

  const currentStep = computed(() => {
    return guideSteps.value[currentGuideStep.value] || {} as GuideStep;
  });

  // 样式计算属性
  const highlightStyle = computed<CSSProperties>(() => {
    if (!showUserGuide.value || !currentStep.value.element) {
      return {};
    }

    const element = document.querySelector(currentStep.value.element);
    if (!element) {
      return {};
    }

    const rect = element.getBoundingClientRect();
    return {
      position: 'fixed',
      top: `${rect.top}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
      borderRadius: '4px',
      border: '2px solid #409EFF',
      boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
      pointerEvents: 'none',
      zIndex: '9998',
      transition: 'all 0.3s ease'
    };
  });

  const guideCardStyle = computed<CSSProperties>(() => {
    if (!showUserGuide.value || !currentStep.value.element) {
      return {};
    }

    const element = document.querySelector(currentStep.value.element);
    if (!element) {
      return {};
    }

    const rect = element.getBoundingClientRect();
    const cardWidth = 400;
    const cardHeight = 240; // 稍微减小预估高度以获得更好的定位
    const padding = 20;

    // 默认显示在元素右侧
    let top = rect.top;
    let left = rect.right + padding;

    // 如果指定了位置，优先使用
    if (currentStep.value.position === 'top') {
      top = rect.top - cardHeight - padding;
      left = rect.left + (rect.width / 2) - (cardWidth / 2);
    } else if (currentStep.value.position === 'bottom') {
      top = rect.bottom + padding;
      left = rect.left + (rect.width / 2) - (cardWidth / 2);
    } else if (currentStep.value.position === 'left') {
      top = rect.top;
      left = rect.left - cardWidth - padding;
    } else if (currentStep.value.position === 'right') {
      top = rect.top;
      left = rect.right + padding;
    } else {
      // 自动计算逻辑 (保持原样作为回退)
      // 如果右侧空间不足，显示在左侧
      if (left + cardWidth > window.innerWidth) {
        left = rect.left - cardWidth - padding;
      }

      // 如果左侧空间也不足，显示在元素下方
      if (left < 0) {
        left = rect.left;
        top = rect.bottom + padding;
      }

      // 如果下方空间不足，显示在元素上方
      if (top + cardHeight > window.innerHeight) {
        top = rect.top - cardHeight - padding;
      }
    }

    // 确保不超出视口
    top = Math.max(padding, Math.min(top, window.innerHeight - cardHeight - padding));
    left = Math.max(padding, Math.min(left, window.innerWidth - cardWidth - padding));

    return {
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      zIndex: '9999',
      transition: 'all 0.3s ease'
    };
  });

  // 方法
  const startGuide = async () => {
    if (!isPageSupportedForGuide(route.path)) {
      console.warn('[GuideStore] 当前页面不支持用户引导:', route.path);
      return;
    }

    const steps = getGuideSteps(route.path);
    guideSteps.value = steps;
    currentGuideStep.value = 0;
    showUserGuide.value = true;
  };

  const completeGuide = () => {
    showUserGuide.value = false;
    currentGuideStep.value = 0;

    // 标记用户已完成引导
    localStorage.setItem('user_guide_completed', 'true');
  };

  const skipGuide = () => {
    showUserGuide.value = false;
    currentGuideStep.value = 0;

    // 标记用户已跳过引导
    localStorage.setItem('user_guide_skipped', 'true');
  };

  const handleNextStep = () => {
    if (currentGuideStep.value < guideSteps.value.length - 1) {
      currentGuideStep.value++;
    } else {
      completeGuide();
    }
  };

  const handlePrevStep = () => {
    if (currentGuideStep.value > 0) {
      currentGuideStep.value--;
    }
  };

  const setGuideStep = (index: number) => {
    if (index >= 0 && index < guideSteps.value.length) {
      currentGuideStep.value = index;
    }
  };

  const checkFirstTimeUser = (): boolean => {
    const completed = localStorage.getItem('user_guide_completed');
    const skipped = localStorage.getItem('user_guide_skipped');
    return !completed && !skipped;
  };

  const checkShouldShowWelcome = (): boolean => {
    const welcomeShown = localStorage.getItem('welcome_dialog_shown');
    return !welcomeShown && checkFirstTimeUser();
  };

  const handleWelcomeStartGuide = async () => {
    showWelcomeDialog.value = false;
    // 等待一小段时间让弹窗关闭动画完成
    await new Promise(resolve => setTimeout(resolve, 300));
    await startGuide();
  };

  const handleWelcomeSkip = () => {
    showWelcomeDialog.value = false;
    localStorage.setItem('user_guide_skipped', 'true');
  };

  const updateGuidePosition = () => {
    // 实现引导位置更新逻辑
  };

  return {
    // 状态
    showUserGuide,
    showWelcomeDialog,
    currentGuideStep,
    guideSteps,
    currentPageTitle,
    currentPageDescription,
    currentStep,

    // 样式
    highlightStyle,
    guideCardStyle,

    // 方法
    startGuide,
    completeGuide,
    skipGuide,
    handleNextStep,
    handlePrevStep,
    setGuideStep,
    updateGuidePosition,
    checkFirstTimeUser,
    checkShouldShowWelcome,
    handleWelcomeStartGuide,
    handleWelcomeSkip
  };
});

